import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../../core/redux/IAppState';

import { Redemption } from './redemption';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../core/redux/api/module';
import { API_REDEEM_ENTITLEMENT } from '../../../core/redux/api/endpoints';
import { ACTION_TYPE } from '../../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../../core/redux/api/circuitBreaker/module';
import { addRedeemedEntitlement } from '../module';
import { Entitlement } from '../models';
import { updateGuest } from '../../module';
import * as R from 'ramda';
import { toExternalCode } from './module';
import { assoc } from '../../../language/module';
import { toLocalISOString } from '../../../language/time';


const KEY_ACTION = ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST,
  ACTION_TYPE.REDEEM_ENTITLEMENT_RESPONSE,
  ACTION_TYPE.REDEEM_ENTITLEMENT_ERROR,
];


/**
 * An action that can be dispatched to redeem a guest's entitlements. Works for both Friends and Family and Direct to
 * Window entitlements.
 * @param redemption - A redemption object with:
 * guestId - The master guest ID for the guest whose entitlements we want redeem
 * entitlementId - The ID of the entitlements we're looking to redeem
 * redemptionDate - Just pass current time here -- you just need to hold on to this value so you can POST the
 * local reference associated with this redemption down the line.
 * redemptionQuantity - The number of items to redeem from this entitlements
 * redemptionExternalCode - this field contains either local ref (dtw or f&f) or notes (negative day correction
 * from view days modal)
 */
export const redeemEntitlementAction = (redemption: Redemption) => makeApiAction(
  {
    method:   'POST',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: API_REDEEM_ENTITLEMENT,
    // Entitlement Description intentionally blank
    body:     (state: IAppState) => JSON.stringify({
      "userId": state.userState.userId,
      "masterGuestId": redemption.guestId,
      "entitlementId": redemption.entitlementId,
      "entitlementDescription": "",
      "redemptionDate": toLocalISOString(redemption.redemptionDate),
      "redemptionQuantity": redemption.redemptionQuantity,
      "redemptionLocation": "ResortTools",
      "redemptionExternalCode": toExternalCode(redemption),
      "redemptionIdReference": redemption.redemptionIdReference ?? null,
      "resort": state.selectedResort.resortAbbreviation,
      "modifiedById": state.userState.userName
    }),
    types:    ACTIONS.map(action => ({
      type: action,
      meta: { redemption },
    })) as RsaaTypes,
    bailout:  (state: IAppState) => !!getLoading(state, redemption.guestId, KEY_ACTION)
                                    || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<number> = (state, action, error, isLoading, payload) => {
  const redemption = R.path<Redemption>([ 'meta', 'redemption' ], action);

  // Preserve redemption in app state so we don't lose it and can reuse it in later requests
  const withRedemption = assoc('redeemedEntitlement', redemption, state);

  // If we're loading, remove the Guest's errors and add a loading state
  if (isLoading) {
    return addLoading(withRedemption, redemption!.guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(withRedemption, KEY_ACTION, !!error);

  // If we've errored, remove the guest's loading state, and add an error state
  if (error) {
    return addError(withBreaker, redemption!.guestId, KEY_ACTION, error);
  }

  // Remove Loading/Error and add redemption ID from request to state
  const noLoadingOrError = removeLoadingAndError(withBreaker, redemption!.guestId, KEY_ACTION);

  const alteredState = R.assocPath([ 'redeemedEntitlement', 'redemptionId' ], payload, noLoadingOrError);

  // We need to update the guest's entitlement numbers, because they've redeemed one. These next ~10 lines are doing that
  const targetGuest = alteredState.guests.get(redemption!.guestId);

  const isFnFEntitlement = !!R.find(
    (entitlement: Entitlement) => entitlement.entitlementId === redemption!.entitlementId,
    R.path([ 'friendsAndFamily' ], targetGuest) || [],
  );

  return updateGuest(
    alteredState,
    redemption!.guestId,
    guest => addRedeemedEntitlement(
      guest,
      redemption!.entitlementId,
      redemption!.redemptionQuantity,
      isFnFEntitlement ? 'friendsAndFamily' : 'directToWindow',
      redemption!.redemptionDate,
      alteredState!.selectedResort.resortName,
      redemption!.redemptionIdReference ?? "",
      redemption!.modifiedById ?? "",
    ),
  );
};


export const redeemEntitlementReducer = makeApiReducer<number>(ACTIONS, KEY_ACTION, reducer);
